import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import { updateUserStats } from '../../services/supabaseService';

/**
 * PwaInstallModal
 * 
 * A self-contained UI component for PWA installation instructions.
 * Manages its own visibility by fetching 'should_show_pwa_popup' from the DB.
 */
const PwaInstallModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    const checkStatus = async () => {
      const { data: { user: sbUser } } = await supabase.auth.getUser();
      if (!sbUser) return;

      setUserId(sbUser.id);

      const { data, error } = await supabase
        .from('users')
        .select('should_show_pwa_popup')
        .eq('id', sbUser.id)
        .maybeSingle();

      if (!error && data?.should_show_pwa_popup) {
        setIsOpen(true);
      }
    };

    checkStatus();
  }, []);

  const handleDismiss = async () => {
    setIsOpen(false);
    if (userId) {
      await updateUserStats(userId, { should_show_pwa_popup: false });
    }
  };

  // Close on ESC key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') handleDismiss();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [userId]);

  if (!isOpen) return null;

  const colors = {
    primary: "#14B8A6",
    primaryLight: "#F0FDFB",
    text: "#0F172A",
    secondaryText: "#64748B",
    mutedText: "#94A3B8",
    background: "#F8FBFA",
    backdrop: "rgba(15, 23, 42, 0.6)",
    divider: "#F1F5F9"
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) handleDismiss();
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: colors.backdrop,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px',
        backdropFilter: 'blur(4px)',
        animation: 'fadeIn 0.2s ease-out'
      }}
      onClick={handleBackdropClick}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '440px',
          backgroundColor: 'white',
          borderRadius: '32px',
          position: 'relative',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          animation: 'slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)'
        }}
      >
        {/* Close Button */}
        <button
          onClick={handleDismiss}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            backgroundColor: '#F1F5F9',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            zIndex: 10,
            transition: 'background-color 0.2s'
          }}
          aria-label="Close"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={colors.secondaryText} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>

        {/* Header Section */}
        <div style={{
          padding: '24px 20px 16px 20px',
          textAlign: 'center',
          backgroundColor: '#F0FDFB', // Soft teal-tinted header
          borderBottom: `1px solid ${colors.divider}`
        }}>
          <h2 style={{
            fontSize: '17px',
            fontWeight: '800',
            color: colors.text,
            margin: '0 0 2px 0',
            lineHeight: 1.2
          }}>
            Add <span style={{ color: colors.primary }}>Verbal</span>Flo App to
          </h2>
          <h1 style={{
            fontSize: '21px',
            fontWeight: '900',
            color: colors.primary,
            margin: '0 0 6px 0',
            lineHeight: 1.1
          }}>
            Your Home Screen
          </h1>
          <p style={{
            fontSize: '13px',
            color: colors.secondaryText,
            margin: 0,
            fontWeight: '500'
          }}>
            Get quick access anytime, anywhere
          </p>
        </div>

        {/* Scrollable Content Area */}
        <div style={{
          padding: '12px 16px 80px 16px',
          overflowY: 'auto',
          flex: 1
        }}>
          {/* iOS Section */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
              <div style={{
                width: '28px', height: '28px', borderRadius: '50%', backgroundColor: colors.primaryLight,
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill={colors.primary}>
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.1 2.48-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.31-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.36 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                </svg>
              </div>
              <h3 style={{ fontSize: '15px', fontWeight: '800', color: colors.text, margin: 0 }}>iPhone / iPad</h3>
            </div>

            <ol style={{ margin: 0, paddingLeft: '16px', color: colors.secondaryText, fontSize: '13px', lineHeight: '1.4' }}>
              <li style={{ marginBottom: '10px' }}>
                Tap the <strong>Share</strong> button
                <span style={{
                  display: 'inline-flex', verticalAlign: 'middle', margin: '0 6px',
                  backgroundColor: colors.primaryLight, padding: '3px', borderRadius: '5px'
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={colors.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path>
                    <polyline points="16 6 12 2 8 6"></polyline>
                    <line x1="12" y1="2" x2="12" y2="15"></line>
                  </svg>
                </span>
                at the bottom
              </li>
              <li style={{ marginBottom: '10px' }}>Scroll down and tap <strong>"Add to Home Screen"</strong></li>
              <li>Tap <strong>"Add"</strong> in the top right</li>
            </ol>
          </div>

          <div style={{ height: '1px', backgroundColor: colors.divider, marginBottom: '20px' }} />

          {/* Android Section */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
              <div style={{
                width: '28px', height: '28px', borderRadius: '50%', backgroundColor: colors.primaryLight,
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill={colors.primary}>
                  <path d="M7 11h2v2H7v-2zm8 0h2v2h-2v-2zm-3.8 6.5l.8-2.8 1.4.5-.8 2.8-1.4-.5zM12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" />
                </svg>
              </div>
              <h3 style={{ fontSize: '15px', fontWeight: '800', color: colors.text, margin: 0 }}>Android</h3>
            </div>

            <ol style={{ margin: 0, paddingLeft: '16px', color: colors.secondaryText, fontSize: '13px', lineHeight: '1.4' }}>
              <li style={{ marginBottom: '10px' }}>
                Tap the <strong>menu</strong> button
                <span style={{
                  display: 'inline-flex', verticalAlign: 'middle', margin: '0 6px',
                  backgroundColor: colors.divider, padding: '2px 5px', borderRadius: '4px'
                }}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" style={{ color: colors.secondaryText }}>
                    <circle cx="12" cy="5" r="2"></circle>
                    <circle cx="12" cy="12" r="2"></circle>
                    <circle cx="12" cy="19" r="2"></circle>
                  </svg>
                </span>
                in Chrome
              </li>
              <li style={{ marginBottom: '10px' }}>Tap <strong>"Add to Home screen"</strong> or <strong>"Install app"</strong></li>
              <li>Tap <strong>"Add"</strong> to confirm</li>
            </ol>
          </div>

          <div style={{ height: '1px', backgroundColor: colors.divider, marginBottom: '20px' }} />

          {/* Desktop Section */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
              <div style={{
                width: '28px', height: '28px', borderRadius: '50%', backgroundColor: colors.primaryLight,
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={colors.primary} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                  <line x1="8" y1="21" x2="16" y2="21"></line>
                  <line x1="12" y1="17" x2="12" y2="21"></line>
                </svg>
              </div>
              <h3 style={{ fontSize: '15px', fontWeight: '800', color: colors.text, margin: 0 }}>Desktop</h3>
            </div>

            <ol style={{ margin: 0, paddingLeft: '16px', color: colors.secondaryText, fontSize: '13px', lineHeight: '1.4' }}>
              <li style={{ marginBottom: '10px' }}>
                Click the browser menu
                <span style={{
                  display: 'inline-flex', verticalAlign: 'middle', margin: '0 6px',
                  backgroundColor: colors.divider, padding: '2px 5px', borderRadius: '4px'
                }}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" style={{ color: colors.secondaryText }}>
                    <circle cx="12" cy="5" r="2"></circle>
                    <circle cx="12" cy="12" r="2"></circle>
                    <circle cx="12" cy="19" r="2"></circle>
                  </svg>
                </span>
                (top right)
              </li>
              <li style={{ marginBottom: '10px' }}>Select <strong>"Install VerbalFlo App"</strong> or <strong>"Add to Dock"</strong></li>
              <li>Click <strong>"Install"</strong> to add it</li>
            </ol>
          </div>
        </div>

        {/* Footer Actions */}
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          padding: '12px 24px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '10px',
          background: 'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.95) 20%, rgba(255,255,255,1) 100%)',
          zIndex: 20
        }}>
          <button
            onClick={handleDismiss}
            style={{
              backgroundColor: colors.primary,
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '24px',
              fontSize: '15px',
              fontWeight: '700',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              cursor: 'pointer',
              boxShadow: '0 6px 10px -3px rgba(20, 184, 166, 0.4)',
              transition: 'transform 0.2s, box-shadow 0.2s'
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5"></path>
              <line x1="9" y1="18" x2="15" y2="18"></line>
              <line x1="10" y1="22" x2="14" y2="22"></line>
            </svg>
            Got it!
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default PwaInstallModal;
